<template>
  <div class="location-container" :class="isPc ? 'pc': 'mobile'">
    <template v-if="isPc">
      <div class="location-select-container pc">
        <el-select
          class="select country-select"
          v-model="internalValue.country"
          :disabled="isDisabled"
          :placeholder="$t('microSite.form.placeholder.country')"
          @change="handelCountryChange"
        >
          <el-option
            v-for="item in countryOption"
            :key="item.index"
            :label="item.text"
            :value="item.value"
          />
        </el-select>
        <el-select
          class="select province-select"
          v-model="internalValue.province"
          :disabled="isDisabled"
          :placeholder="$t('microSite.form.placeholder.province')"
          @change="handelProvinceChange"
        >
          <el-option
            v-for="item in provinceOption"
            :key="item.index"
            :label="item.text"
            :value="item.value"
          />
        </el-select>
        <el-select
          class="select city-select"
          v-model="internalValue.city"
          :disabled="isDisabled"
          :placeholder="$t('microSite.form.placeholder.city')"
        >
          <el-option
            v-for="item in cityOption"
            :key="item.index"
            :label="item.text"
            :value="item.value"
          />
        </el-select>
      </div>
    </template>
    <template v-else>
      <van-field
        v-model="fieldValue"
        is-link
        readonly
        class="mobile-select"
        :class="isDisabled ? 'disabled': ''"
        :disabled="isDisabled"
        :placeholder="$t('microSite.form.placeholder.location')"
        @click="handelClickLocationSelect"
      />
      <van-popup v-model="showLocationSelect" round position="bottom">
        <van-cascader
          v-model="cascaderValue"
          :title="$t('microSite.form.placeholder.location')"
          :options="mobileOption"
          @close="showLocationSelect = false"
          @change="onChange"
          @finish="onFinish"
        />
      </van-popup>
    </template>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'

export default {
  props: {
    value: {
    },
    title: {
      type: String,
      default: ''
    },
    isPc: {
      type: Boolean,
      default: true
    },
    isRequired: {
      type: Boolean,
      default: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      internalValue: {
        country: '',
        province: '',
        city: ''
      },
      countryOption: [],
      provinceOption: [],
      cityOption: [],
      mobileOption: [],
      showLocationSelect: false,
      fieldValue: '',
      cascaderValue: ''
    }
  },
  computed: {
  },
  async created () {
    this.internalValue = (_.isNil(this.value) || this.value === '') ? { country: '', province: '', city: '' } : this.value
    await this.loadCountryList()
    if (this.internalValue.country) {
      await this.loadProvinceList()
    }
    if (this.internalValue.province) {
      await this.loadCityList()
    }
    if (!this.isPc) {
      this.initMobileLocationSelect()
    }
  },
  mounted () {
  },
  methods: {
    ...mapActions('worldLocation', ['getCountryList', 'getProvinceList', 'getCityList']),
    formatSimpleInfo (infoItem) {
      const info = {
        ...infoItem,
        value: infoItem.id || infoItem.value
      }
      addI18nProperty(info, 'text', 'cn_name', 'name')
      return info
    },
    async loadCountryList () {
      const locationList = await this.getCountry()
      this.countryOption = _.map(locationList, this.formatSimpleInfo)
      this.mobileOption = _.map(locationList, (infoItem) => {
        const info = {
          ...infoItem,
          value: infoItem.id || infoItem.value,
          children: [],
          type: 'country'
        }
        addI18nProperty(info, 'text', 'cn_name', 'name')
        return info
      })
    },
    async getCountry () {
      const resp = await this.getCountryList()
      if (resp) {
        return resp.location_list
      }
      return []
    },
    async loadProvinceList () {
      if (this.internalValue.country && !isNaN(this.internalValue.country)) {
        const locationList = await this.getProvince(this.internalValue.country)
        this.provinceOption = _.map(locationList, this.formatSimpleInfo)
      }
    },
    async getProvince (countryId) {
      const resp = await this.getProvinceList({ parentId: countryId })
      if (resp) {
        return resp.location_list
      }
      return []
    },
    async loadCityList () {
      if (this.internalValue.province && !isNaN(this.internalValue.province)) {
        const locationList = await this.getCity(this.internalValue.province)
        this.cityOption = _.map(locationList, this.formatSimpleInfo)
      }
    },
    async getCity (provinceId) {
      const resp = await this.getCityList({ parentId: provinceId })
      if (resp) {
        return resp.location_list
      }
      return []
    },
    async handelCountryChange () {
      this.internalValue.province = ''
      this.internalValue.city = ''
      this.loadProvinceList()
    },
    async handelProvinceChange () {
      this.internalValue.city = ''
      this.loadCityList()
    },
    async onChange ({ value, selectedOptions }) {
      const [options] = selectedOptions.slice(-1)
      let getLocationMethod = null
      if (options.type === 'country') {
        getLocationMethod = this.getProvince
      } else if (options.type === 'province') {
        getLocationMethod = this.getCity
      }
      if (getLocationMethod) {
        const locationList = await getLocationMethod(value)
        options.children = _.map(locationList, (infoItem) => {
          const info = {
            ...infoItem,
            value: infoItem.id || infoItem.value
          }
          addI18nProperty(info, 'text', 'cn_name', 'name')
          if (options.type === 'country') {
            info.children = []
            info.type = 'province'
          }
          return info
        })
      }
    },
    onFinish ({ selectedOptions }) {
      this.showLocationSelect = false
      this.fieldValue = selectedOptions.map((option) => option.text).join('/')
      _.forEach(selectedOptions, (item) => {
        if (item.type === 'country') {
          this.internalValue.country = item.value
        } else if (item.type === 'province') {
          this.internalValue.province = item.value
        } else {
          this.internalValue.city = item.value
        }
      })
    },
    handelClickLocationSelect () {
      if (!this.isDisabled) {
        this.showLocationSelect = true
      }
    },
    async initMobileLocationSelect () {
      const countryId = this.internalValue.country
      const provinceId = this.internalValue.province
      const cityId = this.internalValue.city
      let selectCounty = null
      let selectProvince = null
      let selectCity = null
      if (countryId) {
        selectCounty = _.find(this.mobileOption, (item) => { return item.id === countryId })
      }
      if (selectCounty) {
        const locationList = await this.getProvince(countryId)
        selectCounty.children = _.map(locationList, (infoItem) => {
          const info = {
            ...infoItem,
            value: infoItem.id || infoItem.value,
            children: [],
            type: 'province'
          }
          addI18nProperty(info, 'text', 'cn_name', 'name')
          return info
        })
        selectProvince = _.find(selectCounty.children, (item) => { return item.id === provinceId })
      }
      if (selectProvince) {
        const locationList = await this.getCity(provinceId)
        selectProvince.children = _.map(locationList, this.formatSimpleInfo)
        selectCity = _.find(selectProvince.children, (item) => { return item.id === cityId })
        this.fieldValue = `${selectCounty.text}/${selectProvince.text}/${selectCity.text}`
      }
      this.cascaderValue = cityId
    }
  },
  watch: {
    value: {
      handler (val, oldVal) {
        this.internalValue = (_.isNil(val) || val === '') ? { country: '', province: '', city: '' } : val
      },
      immediate: true
    },
    internalValue (val) {
      this.$emit('input', val)
    },
    'internalValue.country' (val) {
      this.loadProvinceList()
    },
    'internalValue.province' (val) {
      this.loadCityList()
    }
  }
}
</script>

<style lang="scss" scoped>
.location-container {
  padding: 10px 0 0 0;

  &.pc {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .mobile-select {
    padding: 3px 10px;
    line-height: 24px;
    border: 1px solid #DCDFE6;

    &.disabled {
      background: #F5F7FA;
    }
  }

  .location-select-container {

    display: flex;
    justify-content: space-between;

    &.pc {
      width: 35%;
    }

    .select {
      width: 30%;
    }
  }
}
</style>
